/**
 * A5 用户查询 — 搜索入口页
 *
 * Stream 2 Phase 2-4b / Plan §10 Stream 2 / Design §4.2 A5 + §5.7 boost 反查 unionId.
 *
 * 功能：
 *  - 用户搜索 form（q + searchBy 智能识别/精确）+ 结果表格（userId 点击跳详情）
 *  - PII 提示 Alert（phone/unionId 需对应权限）
 *  - status Tag 颜色：active 绿 / frozen 红 / recycling 黄 / deleted 灰
 *  - 分页
 *  - 第二个 Card：按 unionId 反查 boost 关系（PermissionGate BOOST_READ_RELATIONSHIPS）
 *
 * 整页 PermissionGate USER_READ。
 */
import { useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Table,
  Tag,
  Alert,
  Space,
  Result,
  Typography,
  Descriptions,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  searchUsers,
  getBoostByUnionId,
  type UserSearchItem,
  type UserStatus,
  type SearchBy,
  type BoostRel,
} from '../../../api/users';

const { Text } = Typography;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function userStatusColor(status: UserStatus): string {
  switch (status) {
    case 'active':    return 'green';
    case 'frozen':    return 'red';
    case 'recycling': return 'gold';
    case 'deleted':   return 'default';
    default:          return 'default';
  }
}

function userStatusLabel(status: UserStatus): string {
  switch (status) {
    case 'active':    return '正常';
    case 'frozen':    return '已冻结';
    case 'recycling': return '回收中';
    case 'deleted':   return '已删除';
    default:          return status;
  }
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ---------------------------------------------------------------------------
// 按 unionId 反查 boost 关系 Card
// ---------------------------------------------------------------------------

function BoostReverseCard() {
  const [form] = Form.useForm<{ unionId: string }>();
  const [result, setResult] = useState<{ relationships: BoostRel[]; matchedInviteeCount: number } | null>(null);

  const mutation = useMutation({
    mutationFn: (unionId: string) => getBoostByUnionId(unionId),
    onSuccess: (data) => {
      setResult(data);
    },
    onError: (err: any) => {
      const m = err?.response?.data?.message ?? '反查失败，请重试';
      message.error(m);
    },
  });

  function handleSearch() {
    form.validateFields().then((vals) => {
      mutation.mutate(vals.unionId.trim());
    }).catch(() => undefined);
  }

  const boostRelColumns: ColumnsType<BoostRel> = [
    { title: '关系 ID', dataIndex: 'relationId', key: 'relationId' },
    { title: '邀请人 ID', dataIndex: 'inviterId', key: 'inviterId' },
    { title: '被邀请人 ID', dataIndex: 'inviteeId', key: 'inviteeId' },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => formatDateTime(v),
    },
  ];

  return (
    <PermissionGate
      point={PERMISSIONS.BOOST_READ_RELATIONSHIPS}
      fallback={
        <Card
          title="按 unionId 反查 boost 关系"
          style={{ marginBottom: 16 }}
          data-testid="boost-reverse-card"
        >
          <Alert
            type="warning"
            showIcon
            message="需要 boost:read_relationships 权限"
            data-testid="boost-reverse-no-perm"
          />
        </Card>
      }
    >
      <Card
        title="按 unionId 反查 boost 关系"
        style={{ marginBottom: 16 }}
        data-testid="boost-reverse-card"
      >
        <Form form={form} layout="inline" style={{ marginBottom: 16 }}>
          <Form.Item
            name="unionId"
            rules={[{ required: true, message: '请输入 unionId' }]}
          >
            <Input
              placeholder="请输入 unionId"
              style={{ width: 300 }}
              data-testid="boost-reverse-input"
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              onClick={handleSearch}
              loading={mutation.isPending}
              data-testid="boost-reverse-btn"
            >
              反查
            </Button>
          </Form.Item>
        </Form>

        {result !== null && (
          <div data-testid="boost-reverse-result">
            <Descriptions size="small" style={{ marginBottom: 12 }}>
              <Descriptions.Item label="匹配被邀请人数">
                {result.matchedInviteeCount}
              </Descriptions.Item>
            </Descriptions>
            <Table<BoostRel>
              rowKey="relationId"
              columns={boostRelColumns}
              dataSource={result.relationships}
              size="small"
              pagination={false}
              data-testid="boost-reverse-table"
            />
          </div>
        )}
      </Card>
    </PermissionGate>
  );
}

// ---------------------------------------------------------------------------
// Main search page (inner, after permission check)
// ---------------------------------------------------------------------------

interface SearchFormValues {
  q: string;
  searchBy?: SearchBy | '';
}

function UsersSearchPageInner() {
  const [form] = Form.useForm<SearchFormValues>();
  const [searchParams, setSearchParams] = useState<{ q: string; searchBy?: SearchBy } | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const query = useQuery({
    queryKey: ['users-search', searchParams, page, pageSize],
    queryFn: () =>
      searchUsers({
        q: searchParams!.q,
        searchBy: searchParams!.searchBy,
        page,
        pageSize,
      }),
    enabled: searchParams !== null,
  });

  function handleSearch() {
    form.validateFields().then((vals) => {
      setSearchParams({
        q: vals.q.trim(),
        searchBy: (vals.searchBy as SearchBy) || undefined,
      });
      setPage(1);
    }).catch(() => undefined);
  }

  const columns: ColumnsType<UserSearchItem> = [
    {
      title: '用户 ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 180,
      render: (userId: string) => (
        <Link to={`/users/${encodeURIComponent(userId)}`} data-testid={`user-link-${userId}`}>
          {userId}
        </Link>
      ),
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 140,
    },
    {
      title: '手机号（脱敏）',
      dataIndex: 'phoneMasked',
      key: 'phoneMasked',
      width: 140,
    },
    {
      title: 'unionId（脱敏）',
      dataIndex: 'unionIdMasked',
      key: 'unionIdMasked',
      width: 180,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (s: UserStatus) => (
        <Tag color={userStatusColor(s)} data-testid={`status-tag-${s}`}>
          {userStatusLabel(s)}
        </Tag>
      ),
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (v: string) => formatDateTime(v),
    },
  ];

  return (
    <div data-testid="users-search-page">
      {/* 搜索 Card */}
      <Card title="用户查询（A5）" style={{ marginBottom: 16 }}>
        {/* PII 提示 */}
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="phone / unionId 搜索需要对应 PII 权限（pii:read_phone / pii:read_address）"
          data-testid="pii-hint-alert"
        />

        <Form
          form={form}
          layout="inline"
          style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}
          data-testid="users-search-form"
        >
          <Form.Item
            name="q"
            rules={[{ required: true, message: '请输入搜索关键词' }]}
          >
            <Input
              placeholder="用户 ID / 手机号 / 昵称 / unionId"
              style={{ width: 300 }}
              data-testid="users-search-q"
              allowClear
              onPressEnter={handleSearch}
            />
          </Form.Item>

          <Form.Item name="searchBy">
            <Select
              placeholder="智能识别"
              allowClear
              style={{ width: 140 }}
              data-testid="users-search-by"
              options={[
                { value: 'userId',   label: '用户ID' },
                { value: 'phone',    label: '手机号' },
                { value: 'nickname', label: '昵称' },
                { value: 'unionId',  label: 'unionId' },
              ]}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={handleSearch}
                loading={query.isFetching}
                data-testid="users-search-btn"
              >
                搜索
              </Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 结果表格 */}
        {searchParams !== null && (
          <Table<UserSearchItem>
            rowKey="userId"
            columns={columns}
            dataSource={query.data?.items ?? []}
            loading={query.isLoading}
            pagination={{
              current: page,
              pageSize,
              total: query.data?.total ?? 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p);
                setPageSize(ps);
              },
            }}
            data-testid="users-search-table"
          />
        )}

        {searchParams === null && (
          <Text type="secondary" data-testid="users-search-empty-hint">
            请输入关键词后点击搜索
          </Text>
        )}
      </Card>

      {/* unionId 反查 boost 关系 Card */}
      <BoostReverseCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Export: page-level PermissionGate (USER_READ)
// ---------------------------------------------------------------------------

export default function UsersSearchPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.USER_READ}
      fallback={
        <Result
          status="403"
          title="无权访问"
          subTitle="您没有访问用户查询的权限，请联系管理员申请 user:read 权限。"
          data-testid="users-403"
        />
      }
    >
      <UsersSearchPageInner />
    </PermissionGate>
  );
}
